<script setup lang="ts">
import { computed } from 'vue';
import type { TagKind } from '@/composables/api/useTagCatalog';

/**
 * A tag chip. When the tag's catalog `kind` is known the FONT color encodes
 * it — regular track tags blue, artist tags brown, user tags green — on a
 * neutral chip background. Without a kind (catalog still loading, or a tag
 * not in the catalog) the chip falls back to the legacy name-hashed palette
 * so the same tag is always the same color.
 */
const props = defineProps<{
    name: string;
    /** Catalog-derived kind driving the font color; null/undefined = legacy palette. */
    kind?: TagKind | null;
}>();

const PALETTE = [
    'bg-violet-500/20 text-violet-900 dark:text-violet-200',
    'bg-sky-500/20 text-sky-900 dark:text-sky-200',
    'bg-emerald-500/20 text-emerald-900 dark:text-emerald-200',
    'bg-amber-500/20 text-amber-900 dark:text-amber-300',
    'bg-rose-500/20 text-rose-900 dark:text-rose-200',
    'bg-teal-500/20 text-teal-900 dark:text-teal-200',
    'bg-indigo-500/20 text-indigo-900 dark:text-indigo-200',
    'bg-fuchsia-500/20 text-fuchsia-900 dark:text-fuchsia-200',
];

/** Neutral chip, kind-colored font. Brown has no Tailwind hue — amber-800/500 is the closest. */
const KIND_CLASSES: Record<TagKind, string> = {
    track: 'bg-muted text-blue-700 dark:text-blue-400',
    artist: 'bg-muted text-amber-800 dark:text-amber-500',
    user: 'bg-muted text-green-700 dark:text-green-400',
};

function hashName(value: string): number {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = (hash * 31 + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
}

const colorClass = computed(() =>
    props.kind ? KIND_CLASSES[props.kind] : PALETTE[hashName(props.name) % PALETTE.length],
);
</script>

<template>
    <span
        class="inline-flex items-center rounded-full px-1.5 py-0.5 text-[11px] font-medium leading-none"
        :class="colorClass"
        data-testid="tag-badge"
        :data-kind="kind ?? undefined"
    >
        {{ name }}
    </span>
</template>
